/* =============================================================================
 * LOS PERIODOS DE LOS DOCUMENTOS: QUÉ QUINCENAS Y QUÉ MESES SE OFRECEN
 * -----------------------------------------------------------------------------
 * La bitácora quincenal y el registro mensual se pueden generar (y enviar al
 * trabajador) de CUALQUIER periodo, no solo del en curso y el anterior: una
 * corrección puede tocar registros de hasta 60 días atrás, y en un juicio se
 * necesita el periodo que sea (decisión de Iván, 9-oct-2026).
 *
 * Se ofrece desde el mes del PRIMER REGISTRO de ese trabajador —su primera
 * checada o el primer registro que agregó el patrón— o su FECHA DE INGRESO, lo
 * que sea anterior, hasta el periodo en curso. NUNCA un periodo futuro.
 *
 * El mes del primer registro se lee en hora de México (la de las checadas); "hoy"
 * es el día del aparato, como en el resto de la pantalla (quincenaDe).
 *
 * CommonJS, exportado nombre por nombre (como src/logica/checador.js).
 * ========================================================================== */
const { enHoraDeMexico } = require("./checador");

const _MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const _clave = (p) => p.anio * 12 + p.mes0;
const _deClave = (k) => ({ anio: Math.floor(k / 12), mes0: k % 12 });

function _ms(v) {
  if (!v) return NaN;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.getTime === "function") return v.getTime();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  return NaN;
}
// "AAAA-MM-DD" (la fecha de ingreso de la ficha) → {anio, mes0}, o null.
function _mesDeFecha(f) {
  const p = String(f || "").slice(0, 10).split("-").map(Number);
  return p.length === 3 && p[0] > 1900 && p[1] >= 1 && p[1] <= 12 ? { anio: p[0], mes0: p[1] - 1 } : null;
}

// EL PRIMER MES QUE SE OFRECE: el del primer registro (checadas de esa persona y
// horas declaradas de lo que agregó el patrón) o el de su ingreso, lo que sea
// anterior. Sin ninguno de los dos, el mes de hoy. Nunca después del mes de hoy.
function primerMesDisponible(o) {
  const hoy = o && o.hoy instanceof Date ? o.hoy : new Date();
  const actual = { anio: hoy.getFullYear(), mes0: hoy.getMonth() };
  const candidatos = [];
  ((o && o.instantesDeRegistros) || []).forEach((v) => {
    const ms = _ms(v);
    if (!Number.isFinite(ms)) return;
    const mx = enHoraDeMexico(new Date(ms));
    if (mx) candidatos.push({ anio: mx.getFullYear(), mes0: mx.getMonth() });
  });
  const ingreso = _mesDeFecha(o && o.fechaIngreso);
  if (ingreso) candidatos.push(ingreso);
  if (!candidatos.length) return actual;
  const k = Math.min(...candidatos.map(_clave));
  return _deClave(Math.min(k, _clave(actual)));
}

// "septiembre de 2026"
function etiquetaMes(p) { return `${_MESES[p.mes0]} de ${p.anio}`; }
const _ultimoDia = (p) => new Date(p.anio, p.mes0 + 1, 0).getDate();

// LOS MESES QUE SE OFRECEN, del en curso hacia atrás hasta `primero`.
function mesesDisponibles(primero, hoy) {
  const h = hoy instanceof Date ? hoy : new Date();
  const desde = _clave(primero), hasta = h.getFullYear() * 12 + h.getMonth();
  const out = [];
  for (let k = hasta; k >= desde; k--) { const p = _deClave(k); out.push(Object.assign(p, { enCurso: k === hasta })); }
  return out;
}
// LAS QUINCENAS QUE SE OFRECEN DE UN MES: la 1ª siempre (si el mes se ofrece); la
// 2ª solo si ya empezó (en el mes en curso, a partir del día 16).
function quincenasDelMes(p, hoy) {
  const h = hoy instanceof Date ? hoy : new Date();
  const enCurso = p.anio === h.getFullYear() && p.mes0 === h.getMonth();
  const out = [{ mitad: 1, etiqueta: `1ª quincena (1 al 15)`, enCurso: enCurso && h.getDate() <= 15 }];
  if (!enCurso || h.getDate() >= 16) out.push({ mitad: 2, etiqueta: `2ª quincena (16 al ${_ultimoDia(p)})`, enCurso: enCurso && h.getDate() >= 16 });
  return out;
}
// La quincena en curso (la opción por defecto).
function quincenaEnCurso(hoy) {
  const h = hoy instanceof Date ? hoy : new Date();
  return { anio: h.getFullYear(), mes0: h.getMonth(), mitad: h.getDate() <= 15 ? 1 : 2 };
}
// Los días de una quincena: {ini, fin} (fechas locales, como quincenaDe).
function rangoDeQuincena(q) {
  return q.mitad === 1 ? { ini: new Date(q.anio, q.mes0, 1), fin: new Date(q.anio, q.mes0, 15) }
    : { ini: new Date(q.anio, q.mes0, 16), fin: new Date(q.anio, q.mes0 + 1, 0) };
}
// ¿Se puede emitir ese periodo? Ni antes del primero que se ofrece, ni futuro.
function periodoPermitido(o) {
  const hoy = o.hoy instanceof Date ? o.hoy : new Date();
  const k = _clave(o.periodo), primero = _clave(o.primero), actual = hoy.getFullYear() * 12 + hoy.getMonth();
  if (k < primero || k > actual) return false;
  if (o.periodo.mitad === 2 && k === actual && hoy.getDate() < 16) return false;
  return true;
}

exports.primerMesDisponible = primerMesDisponible;
exports.etiquetaMes = etiquetaMes;
exports.mesesDisponibles = mesesDisponibles;
exports.quincenasDelMes = quincenasDelMes;
exports.quincenaEnCurso = quincenaEnCurso;
exports.rangoDeQuincena = rangoDeQuincena;
exports.periodoPermitido = periodoPermitido;
